import { useEffect, useState } from 'react';

import { getProducts } from 'src/services/ReleaseNotesService';
import { ReleaseNotesProduct } from 'src/types/release-notes/ReleaseNotesProduct';

interface UseProductsDataReturn {
	products: ReleaseNotesProduct[];
	loading: boolean;
	error: boolean;
	topLevelProducts: ReleaseNotesProduct[];
	retryProducts: () => void;
}

export const useProductsData = (): UseProductsDataReturn => {
	const [products, setProducts] = useState<ReleaseNotesProduct[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	// Filter out products that appear as sub-products of other products
	const getTopLevelProducts = (): ReleaseNotesProduct[] => {
		// Collect all product IDs that appear as sub-products
		const subProductIds = new Set<number>();
		products.forEach((product) => {
			product.sub_products?.forEach((subProduct) => {
				subProductIds.add(subProduct.id);
			});
		});

		// Return only products that are NOT sub-products of others
		return products.filter((product) => !subProductIds.has(product.id));
	};

	const fetchProducts = async (): Promise<void> => {
		setLoading(true);
		setError(false);

		try {
			const response = await getProducts();
			setProducts(response.data);
			setLoading(false);
			setError(false);
		} catch {
			setLoading(false);
			setError(true);
		}
	};

	const retryProducts = (): void => {
		fetchProducts();
	};

	// Fetch products on component mount
	useEffect(() => {
		fetchProducts();
	}, []);

	return {
		products,
		loading,
		error,
		topLevelProducts: getTopLevelProducts(),
		retryProducts
	};
};
